import { useQuery } from '@tanstack/react-query';
import { ListTenantUsersResponse, listTenantUsersContract } from '@bliks-io/contracts';
import { apiClient } from '@/api/client';
import { tenantKeys } from './useTenants';

/**
 * List tenant users query
 *
 * Fetches all users for a specific tenant (super-admin only).
 * Returns an array of users with their details.
 *
 * @example
 * ```tsx
 * function TenantDetailPage({ tenantId }: { tenantId: string }) {
 *   const { data, isLoading, error } = useListTenantUsers(tenantId);
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error loading users</div>;
 *
 *   return (
 *     <ul>
 *       {data?.map(user => (
 *         <li key={user._id}>{user.firstName} {user.lastName}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export function useListTenantUsers(tenantId: string) {
  return useQuery({
    queryKey: tenantKeys.users(tenantId),
    queryFn: async ({ signal }): Promise<ListTenantUsersResponse> => {
      const path = listTenantUsersContract.path.replace(':tenantId', tenantId);
      const { data } = await apiClient.get<ListTenantUsersResponse>(path, { signal });
      return data;
    },
    enabled: !!tenantId
  });
}
